import { create } from "zustand"

// Which of the quotation editor header's dropdown menus is open, plus what each one contains —
// REQ-359 follow-up (the A / H / 1-6 shortcuts). "shapes" (#1381 review) is the Shapes menu the S
// hotkey now opens, matching Add/Help instead of the R/O bare-letter shortcuts it originally shipped
// with. "splitView" (#1383) is the dual-view control's own menu, which joined the other three so
// the D hotkey can open it and its rows can be picked by digit like everywhere else. It used to be
// the one menu whose TRIGGER was asymmetric — with one pane the same control entered split screen
// instead of opening anything, so it could only ever be open while split screen was active — but
// #1430 removed that: with two ways to get a second view (in this window, or popped out into its
// own) one click cannot stand for both, so the control always opens the menu and the menu exists in
// every state.
//
// Lives in a store rather than in `CustomWebViewerHeader` local state for the same reason
// splitScreen.store does: the header is rendered inside the Apryse shadow DOM by
// `applyViewerHeaderAndTools`, whose `render()` callback captures its props ONCE, so a hotkey
// handler outside it cannot reach `useState`. A store can be written from anywhere and read live.
//
// The header REGISTERS its own menu items here (`registerMenuItems`) instead of the hotkey side
// keeping a parallel list: the header is what builds the menus, so it is the only place that knows
// their order and which entries are disabled — and it stays right as the REQ-292..299 epic enables
// the remaining ones.
export type TViewerHeaderMenu = "add" | "help" | "shapes" | "splitView"

export type TViewerMenuItem = {
	// The antd menu key, so a test (or a future consumer) can tell which action a position maps to.
	key: string
	disabled: boolean
	run: () => void
}

const NO_ITEMS: Record<TViewerHeaderMenu, TViewerMenuItem[]> = { add: [], help: [], shapes: [], splitView: [] }

type Store = {
	openMenu: TViewerHeaderMenu | null
	items: Record<TViewerHeaderMenu, TViewerMenuItem[]>
	setOpenMenu: (menu: TViewerHeaderMenu | null) => void
	toggleMenu: (menu: TViewerHeaderMenu) => void
	// Closes `menu` ONLY if it is the one currently open. antd's hover trigger reports a close for the
	// menu the pointer left and an open for the one it entered, both after their own delay — so an
	// unguarded close can land after the new menu's open and shut the menu the user just moved onto.
	closeMenu: (menu: TViewerHeaderMenu) => void
	registerMenuItems: (menu: TViewerHeaderMenu, items: TViewerMenuItem[]) => void
	// Activates the item at `position` (1-based) of whichever menu is open, exactly as clicking it
	// would — closing the menu first, like the click handlers do. EVERY row closes it, the dual-screen
	// menu's "Auto open floor plan" toggle included: the #1311 design kept that one open so its
	// checkmark could be seen changing, which review rejected (#1383) — a picked row dismisses the
	// menu, and the checkmark is read on the next open.
	//
	// Returns whether a menu was open AT ALL, not whether something ran: while a menu is up the
	// digits belong to it, so a position with no item (or a disabled one, e.g. "Other" until its
	// REQ lands) is swallowed rather than falling through. That also keeps the numbering stable —
	// a disabled entry keeps its number instead of shifting the ones below it.
	pickOpenMenuItem: (position: number) => boolean
}

export const useViewerMenusStore = create<Store>((set, get) => ({
	openMenu: null,
	items: NO_ITEMS,
	setOpenMenu: (openMenu) => set({ openMenu }),
	toggleMenu: (menu) => set((state) => ({ openMenu: state.openMenu === menu ? null : menu })),
	closeMenu: (menu) => set((state) => (state.openMenu === menu ? { openMenu: null } : state)),
	registerMenuItems: (menu, items) => set((state) => ({ items: { ...state.items, [menu]: items } })),
	pickOpenMenuItem: (position) => {
		const { openMenu, items } = get()
		if (!openMenu) return false
		const item = items[openMenu][position - 1]
		if (item && !item.disabled) {
			set({ openMenu: null })
			item.run()
		}
		return true
	},
}))
